import { useEffect, useRef, useState } from 'react'

interface PasswordLockProps {
  onUnlock: (password: string) => Promise<boolean>
  isTimeout?: boolean
  onForgot?: () => Promise<void> | void
}

export default function PasswordLock({ onUnlock, isTimeout = false, onForgot }: PasswordLockProps) {
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [isVerifying, setIsVerifying] = useState(false)
  const [resetRequested, setResetRequested] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (error && !isVerifying) inputRef.current?.focus()
  }, [error, isVerifying])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!password.trim()) {
      setError('Please enter your password')
      return
    }
    setIsVerifying(true)
    setError('')
    try {
      const ok = await onUnlock(password)
      if (!ok) {
        setError('Incorrect password')
        setPassword('')
      }
    } catch {
      setError('Failed to verify password')
    } finally {
      setIsVerifying(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[var(--bg-primary)]">
      <div className="w-full max-w-sm px-6">
        <div className="text-center mb-8">
          <span className="text-2xl font-bold text-[var(--accent-teal)]">wlns.app</span>
        </div>

        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 rounded-full bg-[var(--bg-card)] flex items-center justify-center">
            <svg className="w-8 h-8 text-[var(--accent-teal)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          </div>
        </div>

        <h1 className="text-xl font-semibold text-center text-[var(--text-primary)] mb-2">
          {isTimeout ? 'Session locked' : 'Password protected'}
        </h1>
        <p className="text-sm text-center text-[var(--text-secondary)] mb-4">
          {isTimeout ? 'Locked due to inactivity.' : 'Enter your password to view your log.'}
        </p>

        <form onSubmit={handleSubmit}>
          <input
            ref={inputRef}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="w-full px-4 py-3 text-center text-lg mb-4"
            autoFocus
            disabled={isVerifying}
          />
          {error && <p className="text-sm text-center text-[var(--bac-danger)] mb-4">{error}</p>}
          <button type="submit" disabled={isVerifying} className="w-full btn btn-primary py-3 text-lg">
            {isVerifying ? (
              <span className="flex items-center justify-center gap-2">
                <span className="spinner" /> Verifying…
              </span>
            ) : (
              'Unlock'
            )}
          </button>
        </form>

        {onForgot && (
          <div className="mt-6 text-xs text-center text-[var(--text-secondary)]">
            {resetRequested ? (
              <p>If a backup email is on file, a reset link is on its way — check your inbox (and spam).</p>
            ) : (
              <button
                onClick={async () => {
                  setResetRequested(true)
                  try {
                    await onForgot()
                  } catch {
                    /* always show the same message — no enumeration */
                  }
                }}
                className="text-[var(--accent-teal)] hover:underline"
              >
                Forgot your password?
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
